import api, { apiClient } from './api';
import {
  StudentFee,
  Payment,
  CreateFeeRequest,
  CreatePaymentRequest,
  FeesListResponse,
  PaymentsListResponse,
  OverdueFeesResponse,
  FeeStatsResponse,
  FeeFilters,
  PaymentFilters
} from '../types/fees';

// Helper function to transform fee data from API format to frontend format
const transformFee = (apiFee: any): StudentFee => ({
  id: apiFee.id,
  studentId: apiFee.studentId,
  studentName: apiFee.studentName,
  amount: parseFloat(apiFee.amount),
  feeDescription: apiFee.feeDescription,
  dueDate: apiFee.dueDate,
  status: apiFee.status,
  createdAt: apiFee.createdAt,
  updatedAt: apiFee.updatedAt,
  student: apiFee.student ? {
    id: apiFee.student.id,
    first_name: apiFee.student.first_name,
    last_name: apiFee.student.last_name,
    email: apiFee.student.email,
    is_active: apiFee.student.is_active
  } : undefined
});

// Helper function to transform payment data from API format to frontend format
const transformPayment = (apiPayment: any): Payment => ({
  id: apiPayment.id,
  feeId: apiPayment.feeId,
  amountPaid: parseFloat(apiPayment.amountPaid),
  paymentDate: apiPayment.paymentDate,
  receiptVoucherNumber: apiPayment.receiptVoucherNumber,
  notes: apiPayment.notes,
  createdAt: apiPayment.createdAt,
  fee: apiPayment.fee ? transformFee(apiPayment.fee) : undefined
});

// Helper function to build query parameters
const buildQueryParams = (params: Record<string, any>): string => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, String(value));
    }
  });
  return queryParams.toString();
};

// Fees API
export const feesApi = {
  /**
   * Get fees with filtering and pagination
   */
  getAll: async (filters: FeeFilters = {}): Promise<FeesListResponse> => {
    try {
      // Map camelCase frontend fields to snake_case backend fields
      const mapSortBy = (sortBy?: string) => {
        switch (sortBy) {
          case 'createdAt': return 'created_at';
          case 'dueDate': return 'due_date';
          case 'studentName': return 'student_name';
          case 'amount': return 'amount';
          default: return 'created_at';
        }
      };

      const queryParams = buildQueryParams({
        status: filters.status,
        student_id: filters.studentId,
        start_date: filters.startDate,
        end_date: filters.endDate,
        page: filters.page || 1,
        limit: filters.limit || 20,
        sort_by: mapSortBy(filters.sortBy),
        sort_order: filters.sortOrder || 'desc'
      });

      const response = await apiClient.get<{
        fees: any[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      }>(`/fees${queryParams ? '?' + queryParams : ''}`);

      return {
        success: true,
        data: {
          fees: response.data.fees.map(transformFee),
          pagination: response.data.pagination
        }
      };
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to fetch fees',
        details: error.details,
        status: error.status
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  /**
   * Get fee by ID
   */
  getById: async (id: number): Promise<{ success: boolean; data: StudentFee }> => {
    try {
      const response = await apiClient.get<StudentFee>(`/fees/${id}`);
      return {
        success: true,
        data: transformFee(response.data)
      };
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to fetch fee',
        details: error.details,
        status: error.status
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  /**
   * Create a new fee
   */
  create: async (feeData: CreateFeeRequest): Promise<{ success: boolean; data: StudentFee }> => {
    try {
      const response = await apiClient.post<StudentFee>('/fees', feeData);
      return {
        success: true,
        data: transformFee(response.data)
      };
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to create fee',
        details: error.details,
        status: error.status
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  /**
   * Update fee details (amount, description, due date)
   */
  update: async (id: number, updates: { amount?: number; feeDescription?: string; dueDate?: string }): Promise<{ success: boolean; message: string; data: StudentFee }> => {
    try {
      const response = await apiClient.put<{ message: string; data: any }>(`/fees/${id}`, updates);
      return {
        success: true,
        message: response.data.message,
        data: transformFee(response.data.data)
      };
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to update fee',
        details: error.details,
        status: error.status
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  /**
   * Update fee status
   */
  updateStatus: async (id: number, status: 'pending' | 'paid' | 'overdue'): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiClient.put<{ message: string }>(`/fees/${id}/status`, { status });
      return {
        success: true,
        message: response.data.message
      };
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to update fee status',
        details: error.details,
        status: error.status
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  /**
   * Get fee statistics
   */
  getStats: async (): Promise<FeeStatsResponse> => {
    try {
      const response = await apiClient.get<{
        totalFees: number;
        totalAmount: number;
        paidFees: number;
        paidAmount: number;
        pendingFees: number;
        pendingAmount: number;
        overdueFees: number;
        overdueAmount: number;
      }>('/fees/stats');

      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to fetch fee statistics',
        details: error.details,
        status: error.status
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  /**
   * Get overdue fees
   */
  getOverdue: async (): Promise<OverdueFeesResponse> => {
    try {
      const response = await apiClient.get<{
        overdueFeees: any[];
        total: number;
      }>('/fees/overdue');

      return {
        success: true,
        data: {
          overdueFeees: response.data.overdueFeees.map(fee => ({
            id: fee.id,
            studentId: fee.studentId,
            studentName: fee.studentName,
            amount: parseFloat(fee.amount),
            feeDescription: fee.feeDescription,
            dueDate: fee.dueDate,
            daysOverdue: fee.daysOverdue
          })),
          total: response.data.total
        }
      };
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to fetch overdue fees',
        details: error.details,
        status: error.status
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  /**
   * Generate monthly fees for all active students
   */
  generateMonthlyFees: async (options?: {
    amount?: number;
    description?: string;
    dueDate?: string;
  }): Promise<{ success: boolean; message: string; data: any }> => {
    try {
      const response = await apiClient.post<{ message: string; data: any }>('/fees/generate-monthly', options);
      return {
        success: true,
        message: response.data.message,
        data: response.data.data
      };
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to generate monthly fees',
        details: error.details,
        status: error.status
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  /**
   * Update overdue fee statuses
   */
  updateOverdueStatuses: async (): Promise<{ success: boolean; message: string; data: any }> => {
    try {
      const response = await apiClient.put<{ message: string; data: any }>('/fees/update-overdue');
      return {
        success: true,
        message: response.data.message,
        data: response.data.data
      };
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to update overdue statuses',
        details: error.details,
        status: error.status
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  resetAll: async (confirmation: string): Promise<{ success: boolean; message: string; data: any }> => {
    try {
      const response = await apiClient.post<{ message: string; data: any }>('/fees/reset', { confirmation });
      return {
        success: true,
        message: response.data.message,
        data: response.data.data
      };
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to reset fees',
        details: error.details,
        status: error.status
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  /**
   * Delete a fee
   */
  delete: async (id: number): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiClient.delete<{ message: string }>(`/fees/${id}`);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to delete fee',
        details: error.details,
        status: error.status
      };
      throw new Error(JSON.stringify(errorObj));
    }
  }
};

// Payments API
export const paymentsApi = {
  /**
   * Get payments with filtering and pagination
   */
  getAll: async (filters: PaymentFilters = {}): Promise<PaymentsListResponse> => {
    try {
      // Map camelCase frontend fields to snake_case backend fields
      const mapPaymentSortBy = (sortBy?: string) => {
        switch (sortBy) {
          case 'createdAt': return 'created_at';
          case 'paymentDate': return 'payment_date';
          case 'amount': return 'amount_paid';
          default: return 'created_at';
        }
      };

      const queryParams = buildQueryParams({
        fee_id: filters.feeId,
        student_id: filters.studentId,
        start_date: filters.startDate,
        end_date: filters.endDate,
        page: filters.page || 1,
        limit: filters.limit || 20,
        sort_by: mapPaymentSortBy(filters.sortBy),
        sort_order: filters.sortOrder || 'desc'
      });

      const response = await apiClient.get<{
        payments: any[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      }>(`/payments${queryParams ? '?' + queryParams : ''}`);

      return {
        success: true,
        data: {
          payments: response.data.payments.map(transformPayment),
          pagination: response.data.pagination
        }
      };
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to fetch payments',
        details: error.details,
        status: error.status
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  /**
   * Get payment by ID
   */
  getById: async (id: number): Promise<{ success: boolean; data: Payment }> => {
    try {
      const response = await apiClient.get<Payment>(`/payments/${id}`);
      return {
        success: true,
        data: transformPayment(response.data)
      };
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to fetch payment',
        details: error.details,
        status: error.status
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  /**
   * Record a new payment
   */
  create: async (paymentData: CreatePaymentRequest): Promise<{ success: boolean; data: any }> => {
    try {
      const response = await apiClient.post<any>('/payments', paymentData);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to record payment',
        details: error.details,
        status: error.status,
        field: error.field,
        code: error.code
      };
      throw errorObj;
    }
  },

  /**
   * Get payments for a specific fee
   */
  getByFeeId: async (feeId: number): Promise<{ success: boolean; data: { payments: Payment[]; totalPaid: number; paymentCount: number } }> => {
    try {
      const response = await apiClient.get<{
        payments: any[];
        totalPaid: number;
        paymentCount: number;
      }>(`/payments/fee/${feeId}`);

      return {
        success: true,
        data: {
          payments: response.data.payments.map(transformPayment),
          totalPaid: response.data.totalPaid,
          paymentCount: response.data.paymentCount
        }
      };
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to fetch payments for fee',
        details: error.details,
        status: error.status
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  /**
   * Generate a unique receipt voucher number
   */
  generateVoucherNumber: async (): Promise<{ success: boolean; data: { receiptVoucherNumber: string } }> => {
    try {
      const response = await apiClient.post<{ receiptVoucherNumber: string }>('/payments/generate-voucher');
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to generate voucher number',
        details: error.details,
        status: error.status
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  /**
   * Validate receipt voucher number uniqueness
   */
  validateVoucherNumber: async (receiptVoucherNumber: string): Promise<{ success: boolean; data: { isUnique: boolean } }> => {
    try {
      const response = await apiClient.post<{ isUnique: boolean }>('/payments/validate-voucher', {
        receiptVoucherNumber
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to validate voucher number',
        details: error.details,
        status: error.status
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  /**
   * Download PDF receipt for a payment
   */
  downloadReceipt: async (paymentId: number, receiptVoucherNumber: string): Promise<void> => {
    try {
      const response = await api.get(`/payments/${paymentId}/receipt`, {
        responseType: 'blob',
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt-${receiptVoucherNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to download receipt',
        details: [error.message]
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  /**
   * Generate a standalone invoice PDF with free-form line items
   */
  generateInvoice: async (invoiceData: {
    invoiceNumber: string;
    invoiceDate: string;
    studentName: string;
    fathersName?: string;
    gradeLevel?: string;
    emiratesId?: string;
    items: { description: string; qty: number; rate: number; amount: number }[];
    paymentMethod?: string;
    transactionNumber?: string;
    transactionDate?: string;
    bankName?: string;
    trnNumber?: string;
    vatRate?: number;
    other?: number;
  }): Promise<void> => {
    try {
      const response = await api.post('/payments/generate-invoice', invoiceData, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceData.invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to generate invoice',
        details: [error.message]
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  /**
   * Download a standalone invoice PDF by its database ID
   */
  downloadInvoiceById: async (invoiceId: number): Promise<void> => {
    try {
      const response = await api.get(`/payments/invoices/${invoiceId}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to download invoice',
        details: [error.message]
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  /**
   * Print a standalone invoice PDF (opens in new tab)
   */
  printInvoiceById: async (invoiceId: number): Promise<void> => {
    try {
      const response = await api.get(`/payments/invoices/${invoiceId}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error: any) {
      throw new Error('Failed to open invoice for printing');
    }
  },

  /**
   * Print a fee payment receipt PDF (opens in new tab)
   */
  printReceiptById: async (paymentId: number): Promise<void> => {
    try {
      const response = await api.get(`/payments/${paymentId}/receipt`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error: any) {
      throw new Error('Failed to open receipt for printing');
    }
  },

  /**
   * Get standalone invoice data as JSON (for editing)
   */
  getInvoiceData: async (invoiceId: number): Promise<{ success: boolean; data: any }> => {
    try {
      const response = await apiClient.get<any>(`/payments/invoices/${invoiceId}/data`);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      throw new Error(error.error || 'Failed to fetch invoice data');
    }
  },

  /**
   * Update a standalone invoice and regenerate PDF
   */
  updateInvoice: async (invoiceId: number, invoiceData: any): Promise<void> => {
    try {
      const response = await api.put(`/payments/invoices/${invoiceId}`, invoiceData, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceData.invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      throw new Error(error.error || 'Failed to update invoice');
    }
  },

  /**
   * Get invoice report data for a date range
   */
  getInvoiceReport: async (startDate: string, endDate: string): Promise<{ success: boolean; data: any }> => {
    try {
      const queryParams = `start_date=${startDate}&end_date=${endDate}`;
      const response = await apiClient.get<any>(`/payments/invoice-report?${queryParams}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to fetch invoice report',
        details: error.details,
        status: error.status
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  /**
   * Download invoice report PDF
   */
  downloadInvoiceReportPDF: async (reportData: { startDate: string; endDate: string; payments: any[]; summary: any }): Promise<void> => {
    try {
      const response = await api.post('/payments/invoice-report/pdf', reportData, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-report-${reportData.startDate}-to-${reportData.endDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to download invoice report PDF',
        details: [error.message]
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  /**
   * Get payment statistics
   */
  getStats: async (): Promise<{ success: boolean; data: any }> => {
    try {
      const response = await apiClient.get<any>('/payments/stats');
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to fetch payment statistics',
        details: error.details,
        status: error.status
      };
      throw new Error(JSON.stringify(errorObj));
    }
  },

  /**
   * Delete a payment
   */
  delete: async (id: number): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiClient.delete<{ message: string }>(`/payments/${id}`);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error: any) {
      const errorObj = {
        error: error.error || 'Failed to delete payment',
        details: error.details,
        status: error.status
      };
      throw new Error(JSON.stringify(errorObj));
    }
  }
};

const feesApiService = {
  fees: feesApi,
  payments: paymentsApi
};

export default feesApiService;
